import { ExtraOptions, Routes } from '@angular/router';
import {
  seoSegmentLiterals,
  seoSegments,
  type CountryCode,
  type SeoSegments,
} from '@handyhand/i18n';

/**
 * Suffixes whose literal varies between countries (e.g. `pris` vs `prijs`)
 * — every supporting country's literal must appear as a leaf in the
 * `:headCategory` subtree, otherwise the first match shadows the other.
 */
type AmbiguousSuffixKey =
  | 'priceGuide'
  | 'guide'
  | 'aiPriceGuide'
  | 'miniService';

function uniqueSuffixLiterals(key: AmbiguousSuffixKey): string[] {
  return [...seoSegmentLiterals('suffix', key)];
}

export const routingConfiguration: ExtraOptions = {
  paramsInheritanceStrategy: 'always',
  onSameUrlNavigation: 'reload',
  scrollPositionRestoration: 'top',
};

export function buildLocalServicePageRoutes(seg: SeoSegments): Routes {
  return [
    {
      path: `${seg.suffix.hubIndex}/:slug/${seg.suffix.localServiceCompany}`,
      loadComponent: () =>
        import('@app/pages/localServices/localCompanyPage/localCompanyPage.component').then(
          (c) => c.LocalServicePageComponent,
        ),
    },
    {
      path: `:subCategory/${seg.suffix.hubIndex}/:slug/${seg.suffix.localServiceCompany}`,
      loadComponent: () =>
        import('@app/pages/localServices/localCompanyPage/localCompanyPage.component').then(
          (c) => c.LocalServicePageComponent,
        ),
    },
    {
      path: `:subCategory/:subSubCategory/${seg.suffix.hubIndex}/:slug/${seg.suffix.localServiceCompany}`,
      loadComponent: () =>
        import('@app/pages/localServices/localCompanyPage/localCompanyPage.component').then(
          (c) => c.LocalServicePageComponent,
        ),
    },
  ];
}

export function buildLocalServicesIndexRoutes(seg: SeoSegments): Routes {
  return [
    {
      path: `:subCategory/${seg.indexPage.localServices}`,
      loadComponent: () =>
        import('../pages/localServices/localServicesIndex/localServicesIndex.component').then(
          (c) => c.LocalServicesIndexComponent,
        ),
    },
    {
      path: `:subCategory/:subSubCategory/${seg.indexPage.localServices}`,
      loadComponent: () =>
        import('../pages/localServices/localServicesIndex/localServicesIndex.component').then(
          (c) => c.LocalServicesIndexComponent,
        ),
    },
    {
      path: `${seg.indexPage.localServices}`,
      loadComponent: () =>
        import('../pages/localServices/localServicesIndex/localServicesIndex.component').then(
          (c) => c.LocalServicesIndexComponent,
        ),
    },
  ];
}

export function buildMiniVerticalIndexRoutes(seg: SeoSegments): Routes {
  return [
    {
      path: `:subCategory/${seg.indexPage.miniVerticals}`,
      loadComponent: () =>
        import('../pages/seo/mini-vertical-index/mini-vertical-index.component').then(
          (c) => c.MiniVerticalIndexComponent,
        ),
    },
    {
      path: `:subCategory/:subSubCategory/${seg.indexPage.miniVerticals}`,
      loadComponent: () =>
        import('../pages/seo/mini-vertical-index/mini-vertical-index.component').then(
          (c) => c.MiniVerticalIndexComponent,
        ),
    },
    {
      path: `${seg.indexPage.miniVerticals}`,
      loadComponent: () =>
        import('../pages/seo/mini-vertical-index/mini-vertical-index.component').then(
          (c) => c.MiniVerticalIndexComponent,
        ),
    },
  ];
}

export function buildTipsIndexRoutes(seg: SeoSegments): Routes {
  return [
    {
      path: `:subCategory/${seg.indexPage.tips}`,
      loadComponent: () =>
        import('../pages/tips-index/tips-index.component').then(
          (c) => c.TipsIndexComponent,
        ),
    },
    {
      path: `:subCategory/:subSubCategory/${seg.indexPage.tips}`,
      loadComponent: () =>
        import('../pages/tips-index/tips-index.component').then(
          (c) => c.TipsIndexComponent,
        ),
    },
    {
      path: `${seg.indexPage.tips}`,
      loadComponent: () =>
        import('../pages/tips-index/tips-index.component').then(
          (c) => c.TipsIndexComponent,
        ),
    },
  ];
}

export function buildAiPriceGuidesIndexRoutes(seg: SeoSegments): Routes {
  return [
    {
      path: `:subCategory/${seg.indexPage.aiPriceGuide}`,
      loadComponent: () =>
        import('../pages/ai-priceguides-index/ai-priceguides-index.component').then(
          (c) => c.AiPriceguidesIndexComponent,
        ),
    },
    {
      path: `:subCategory/:subSubCategory/${seg.indexPage.aiPriceGuide}`,
      loadComponent: () =>
        import('../pages/ai-priceguides-index/ai-priceguides-index.component').then(
          (c) => c.AiPriceguidesIndexComponent,
        ),
    },
    {
      path: `${seg.indexPage.aiPriceGuide}`,
      loadComponent: () =>
        import('../pages/ai-priceguides-index/ai-priceguides-index.component').then(
          (c) => c.AiPriceguidesIndexComponent,
        ),
    },
  ];
}

export function buildIndexPages(seg: SeoSegments): Routes {
  return [
    ...buildLocalServicesIndexRoutes(seg),
    ...buildMiniVerticalIndexRoutes(seg),
    ...buildTipsIndexRoutes(seg),
    ...buildAiPriceGuidesIndexRoutes(seg),
  ];
}

export function buildMainCatRoutes(country: CountryCode): Routes {
  const seg = seoSegments(country);
  return [
    ...buildIndexPages(seg),
    ...buildLocalServicePageRoutes(seg),
    {
      path: `:subCategory/:subSubCategory/${seg.suffix.hubIndex}`,
      loadComponent: () =>
        import('../pages/hub-page/hub-page.component').then(
          (c) => c.HubPageComponent,
        ),
      data: {
        isHubPage: true,
      },
    },
    {
      path: `:subCategory/${seg.suffix.hubIndex}`,
      loadComponent: () =>
        import('../pages/hub-page/hub-page.component').then(
          (c) => c.HubPageComponent,
        ),
      data: {
        isHubPage: true,
      },
    },
    {
      path: seg.suffix.hubIndex,
      loadComponent: () =>
        import('../pages/hub-page/hub-page.component').then(
          (c) => c.HubPageComponent,
        ),
      data: {
        isHubPage: true,
      },
    },
  ];
}

export function buildCityRegionRoutes(seg: SeoSegments): Routes {
  return [
    {
      path: `:region/${seg.suffix.serviceVertical}/${seg.suffix.region}`,
      loadComponent: () =>
        import('../pages/seo/seo.component').then((c) => c.SeoComponent),
    },
    {
      path: `:region/:city/${seg.suffix.serviceVertical}/${seg.suffix.city}`,
      loadComponent: () =>
        import('../pages/seo/seo.component').then((c) => c.SeoComponent),
    },
    {
      path: `${seg.suffix.serviceVertical}`,
      loadComponent: () =>
        import('../pages/seo/seo.component').then((c) => c.SeoComponent),
    },
    {
      path: '',
      loadComponent: () =>
        import('../components/shared/error/error.component').then(
          (m) => m.ErrorComponent,
        ),
    },
  ];
}

export function buildMiniServiceRoutes(): Routes {
  return uniqueSuffixLiterals('miniService').map((suffix) => ({
    path: `:verticalSlug/${suffix}`,
    loadComponent: () =>
      import('../pages/seo/mini-vertical/mini-vertical.component').then(
        (c) => c.MiniVerticalComponent,
      ),
    data: {
      isFindPage: true,
    },
  }));
}

export function buildServiceRoutes(seg: SeoSegments): Routes {
  const cityRegion = buildCityRegionRoutes(seg);
  return [
    {
      path: ':subCategory',
      data: {
        isFindPage: true,
      },
      children: cityRegion,
    },
    {
      path: '',
      data: {
        isFindPage: true,
      },
      children: cityRegion,
    },
  ];
}

export function buildGuideRoutes(seg: SeoSegments): Routes {
  return [
    {
      path: `:guideSlug/${seg.suffix.article}`,
      loadComponent: () =>
        import('@app/pages/blog/details/blog.detail.component').then(
          (m) => m.BlogDetailComponent,
        ),
    },
    ...uniqueSuffixLiterals('guide').map((suffix) => ({
      path: `:guideSlug/${suffix}`,
      loadComponent: () =>
        import('@app/pages/guides/guide/guide.component').then(
          (m) => m.GuideComponent,
        ),
    })),
    ...uniqueSuffixLiterals('aiPriceGuide').map((suffix) => ({
      path: `:guideSlug/${suffix}`,
      loadComponent: () =>
        import('@app/pages/guides/priceGuides/ai-price-guide/ai-price-guide.component').then(
          (m) => m.AiPriceGuideComponent,
        ),
    })),
    ...uniqueSuffixLiterals('priceGuide').map((suffix) => ({
      path: `:guideSlug/${suffix}`,
      loadComponent: () =>
        import('@app/pages/guides/priceGuides/priceGuide.component').then(
          (m) => m.PriceGuideComponent,
        ),
    })),
    {
      path: `:guideSlug/${seg.suffix.tips}`,
      loadComponent: () =>
        import('@app/pages/guides/tips/tips-details/tips-details.component').then(
          (m) => m.TipsDetailsComponent,
        ),
    },
  ];
}

export function buildMainGuideRoutes(seg: SeoSegments): Routes {
  const guide = buildGuideRoutes(seg);
  const cityRegion = buildCityRegionRoutes(seg);
  const miniService = buildMiniServiceRoutes();
  return [
    ...guide,
    {
      path: ':subCategory/:subSubCategory',
      data: {
        isFindPage: true,
      },
      children: [...cityRegion, ...guide],
    },
    {
      path: ':subCategory',
      children: [
        ...guide,
        ...miniService,
        {
          path: '',
          data: {
            isFindPage: true,
          },
          children: cityRegion,
        },
      ],
    },
  ];
}

/**
 * Bare guide suffixes directly under `:headCategory`. Guide pages strip
 * duplicate consecutive segments (`/handyman/handyman/pris` →
 * `/handyman/pris`), so the stripped form must resolve too — the components
 * fall back to `:headCategory` as the guide slug.
 */
export function buildBareGuideSuffixRoutes(): Routes {
  return [
    ...uniqueSuffixLiterals('aiPriceGuide').map((suffix) => ({
      path: suffix,
      loadComponent: () =>
        import('@app/pages/guides/priceGuides/ai-price-guide/ai-price-guide.component').then(
          (m) => m.AiPriceGuideComponent,
        ),
    })),
    ...uniqueSuffixLiterals('priceGuide').map((suffix) => ({
      path: suffix,
      loadComponent: () =>
        import('@app/pages/guides/priceGuides/priceGuide.component').then(
          (m) => m.PriceGuideComponent,
        ),
    })),
  ];
}

export function buildSeoRoutes(country: CountryCode): Routes {
  const seg = seoSegments(country);
  return [
    {
      path: seg.suffix.serviceVertical,
      loadComponent: () =>
        import('../pages/seo/seo.component').then((c) => c.SeoComponent),
      data: {
        isFindPage: true,
      },
    },
    {
      path: '',
      children: [
        ...buildBareGuideSuffixRoutes(),
        ...buildMiniServiceRoutes(),
        ...buildServiceRoutes(seg),
        ...buildMainGuideRoutes(seg),
        {
          path: '**',
          loadComponent: () =>
            import('../components/shared/error/error.component').then(
              (m) => m.ErrorComponent,
            ),
        },
      ],
    },
  ];
}
